import React, { useState, useMemo } from "react";
import { Search, Plus, FileSpreadsheet, FileText, Pencil, Trash2, Paperclip, FileDown, Printer } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { exportCSV, exportXLSX } from "../lib/exportUtils";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { applyFilter, useHSE } from "../lib/store";
import AttachmentUploader from "./attachments/AttachmentUploader";

// Reusable corporate register page. Every ISO 45001 register uses this component.
// Includes: title/subtitle, search, filters (via useHSE), Add/Edit dialog, CSV/Excel/PDF export,
// attachment placeholder field, status/priority badges, empty state, responsive table.
export default function RegisterPage({
  addDisabled = false,
  addDisabledReason = "",
  title,
  subtitle,
  storeKey,
  columns,
  formFields,
  idPrefix,
  badgeMap = {},
  toolbarExtras,
  topPanels,
  showAttachments = true,
  pdfSubject,
  onBeforeSave,
  onSaveRecord,
  onUpdateRecord,
  isRecordLocked,
}) {
  const { data, setData, persistData, filters } = useHSE();
  const rows = useMemo(() => data[storeKey] || [], [data, storeKey]);

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [editingIdentity, setEditingIdentity] = useState(null);
  const [open, setOpen] = useState(false);
  const [attachmentsBusy, setAttachmentsBusy] = useState(false);
  const [saveError, setSaveError] = useState("");

  const dateField = useMemo(() => {
    if (columns.find((c) => c.key === "date")) return "date";
    if (columns.find((c) => c.key === "dateReceived")) return "dateReceived";
    if (columns.find((c) => c.key === "meetingDate")) return "meetingDate";
    return "date";
  }, [columns]);

  const filtered = useMemo(() => {
    return applyFilter(rows, { ...filters, search: filters.search || search }, dateField);
  }, [rows, filters, search, dateField]);

  const rowId = (r) => r?.id ?? r?.docNo ?? r?.number;
  const recordIdentity = (record) => {
    if (record?.id !== undefined) return { key: "id", value: record.id };
    if (record?.docNo !== undefined) return { key: "docNo", value: record.docNo };
    if (record?.number !== undefined) return { key: "number", value: record.number };
    return null;
  };
  const cloneRecord = (record) => JSON.parse(JSON.stringify(record));
  const lockedByDefault = (record) => {
    const status = String(record?.status || record?.workflowStatus || "").trim().toLowerCase();
    return Boolean(record?.isImmutable || record?.immutable || record?.lockedAt || record?.locked_at)
      || ["approved", "published", "locked"].includes(status);
  };
  const recordIsLocked = (record) => typeof isRecordLocked === "function"
    ? Boolean(isRecordLocked(record))
    : lockedByDefault(record);

  const addNew = () => {
    const blank = formFields.reduce((a, f) => ({ ...a, [f.key]: f.default ?? "" }), {});
    blank.id = `${idPrefix}-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`;
    setEditing(blank);
    setEditingIdentity(null);
    setSaveError("");
    setOpen(true);
  };

  const beginEdit = (record) => {
    if (recordIsLocked(record)) {
      toast.info("This record is locked and cannot be edited.");
      return;
    }
    setEditing(cloneRecord(record));
    setEditingIdentity(recordIdentity(record));
    setAttachmentsBusy(false);
    setSaveError("");
    setOpen(true);
  };

  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!editing) return;
    setSaveError("");
    const isEdit = Boolean(editingIdentity);
    const candidate = editingIdentity
      ? { ...editing, [editingIdentity.key]: editingIdentity.value }
      : editing;
    if (onBeforeSave) {
      const errorResult = onBeforeSave(candidate, data, setData);
      if (errorResult) {
        const message = typeof errorResult === "string" ? errorResult : errorResult.message || "Validation failed.";
        setSaveError(message);
        toast.error(message);
        return;
      }
    }
    try {
      setSaving(true);
      const handler = isEdit ? onUpdateRecord : onSaveRecord;
      const serverRecord = handler
        ? await handler(candidate, data, { mode: isEdit ? "edit" : "add", originalIdentity: editingIdentity })
        : null;
      let targetRecord = serverRecord || candidate;
      if (editingIdentity) targetRecord = { ...targetRecord, [editingIdentity.key]: editingIdentity.value };
      const targetId = editingIdentity?.value ?? rowId(targetRecord);
      const exists = rows.some((record) => rowId(record) === targetId);
      if (isEdit && !exists) throw new Error("The record is no longer available. Refresh the register and try again.");
      const next = isEdit
        ? rows.map((record) => (rowId(record) === targetId ? targetRecord : record))
        : [targetRecord, ...rows];
      const nextData = { ...data, [storeKey]: next };
      if (persistData) await persistData(nextData);
      else setData(nextData);
      setOpen(false);
      setEditing(null);
      setEditingIdentity(null);
      toast.success(isEdit ? "Record updated" : "Record added");
    } catch (err) {
      const reference = err?.correlationId ? ` Reference: ${err.correlationId}` : "";
      const message = `${err?.message || err?.code || "Save failed."}${reference}`;
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const remove = (id) => {
    const record = rows.find((row) => rowId(row) === id);
    const attachmentIds = (Array.isArray(record?.attachmentsList) ? record.attachmentsList : []).map((item) => item.id).filter(Boolean);
    const preservationNote = attachmentIds.length ? " Historical attachment objects will be preserved." : "";
    if (!confirm(`Delete this legacy record?${preservationNote}`)) return;
    setData({ ...data, [storeKey]: rows.filter((r) => rowId(r) !== id) });
    toast.success("Record deleted");
  };

  const cancelEdit = () => {
    setOpen(false);
    setEditing(null);
    setEditingIdentity(null);
    setAttachmentsBusy(false);
    setSaveError("");
  };

  const exportPdf = () => {
    if (!filtered.length) { toast.error("No records to export"); return; }
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(8, 43, 77);
    doc.rect(0, 0, pageWidth, 60, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(pdfSubject || title, 24, 26);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Corporate ISO 45001 HSE Report · ${new Date().toLocaleString()}`, 24, 44);
    doc.setTextColor(30, 30, 30);

    // Table
    const colKeys = columns.slice(0, 8);
    const startY = 84;
    const rowH = 20;
    const colW = (pageWidth - 40) / colKeys.length;
    doc.setFillColor(24, 60, 85);
    doc.rect(20, startY - 16, pageWidth - 40, 20, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    colKeys.forEach((c, i) => doc.text(String(c.label).slice(0, 20), 24 + i * colW, startY - 2));
    doc.setTextColor(30, 30, 30);
    doc.setFont("helvetica", "normal");
    filtered.slice(0, 20).forEach((r, ri) => {
      if (ri % 2) { doc.setFillColor(245, 247, 250); doc.rect(20, startY + ri * rowH, pageWidth - 40, rowH, "F"); }
      colKeys.forEach((c, i) => {
        const v = String(r[c.key] ?? "").slice(0, 28);
        doc.text(v, 24 + i * colW, startY + ri * rowH + 13);
      });
    });
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Records: ${filtered.length}  |  Generated by HSE Command Center — Confidential`, 24, doc.internal.pageSize.getHeight() - 16);
    doc.save(`${storeKey}-register.pdf`);
    toast.success("PDF exported");
  };

  const renderCell = (col, row) => {
    if (col.render) return col.render(row);
    const val = row[col.key];
    if (badgeMap[col.key]) {
      const m = badgeMap[col.key][val] || { bg: "#F1F5F9", color: "#455560" };
      return (
        <span className="cmd-pill" style={{ background: m.bg, color: m.color }}>
          {val || "—"}
        </span>
      );
    }
    if (val === undefined || val === null || val === "") return <span className="text-slate-300">—</span>;
    return val;
  };

  return (
    <div className="space-y-5" data-testid={`${storeKey}-page`}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-[#0E2336]">{title}</h1>
          {subtitle && <div className="text-sm text-slate-500 mt-1">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2.5 py-1.5">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              data-testid={`${storeKey}-search`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="text-xs bg-transparent focus:outline-none w-44"
            />
          </div>
          <Button data-testid={`${storeKey}-export-csv`} variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
            onClick={() => { exportCSV(filtered, `${storeKey}.csv`); toast.success("CSV exported"); }}>
            <FileText className="w-3.5 h-3.5" /> CSV
          </Button>
          <Button data-testid={`${storeKey}-export-xlsx`} variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
            onClick={() => { exportXLSX(filtered, `${storeKey}.xlsx`, title); toast.success("Excel exported"); }}>
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </Button>
          <Button data-testid={`${storeKey}-export-pdf`} variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
            onClick={exportPdf}>
            <FileDown className="w-3.5 h-3.5" /> PDF
          </Button>
          <Button data-testid={`${storeKey}-print`} variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
            onClick={() => window.print()}>
            <Printer className="w-3.5 h-3.5" />
          </Button>
          {toolbarExtras}
          <Button
            data-testid={`${storeKey}-add`}
            onClick={() => { if (!addDisabled) addNew(); }}
            disabled={addDisabled}
            title={addDisabled ? addDisabledReason : "Add new record"}
            size="sm"
            className={`h-8 gap-1.5 text-xs font-semibold text-white ${addDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            style={{ background: addDisabled ? "#94A3B8" : "#18B84F" }}
          >
            <Plus className="w-3.5 h-3.5" /> Add New
          </Button>
        </div>
      </div>

      {topPanels}

      <div className="command-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead style={{ background: "#082B4D" }}>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider text-white font-semibold whitespace-nowrap">{c.label}</th>
                ))}
                {showAttachments && <th className="text-center py-2.5 px-3 text-[10px] uppercase tracking-wider text-white font-semibold">Files</th>}
                <th className="text-right py-2.5 px-3 text-[10px] uppercase tracking-wider text-white font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={columns.length + (showAttachments ? 2 : 1)} className="py-16 text-center">
                    <div className="text-slate-300 text-3xl mb-1">📂</div>
                    <div className="text-slate-500 text-sm font-medium">No records found</div>
                    <div className="text-slate-400 text-xs mt-1">Adjust filters or add a new record</div>
                  </td>
                </tr>
              )}
              {filtered.map((r, i) => (
                <tr key={rowId(r) ?? i} className={`border-b border-slate-100 hover:bg-amber-50/40 ${i % 2 ? "bg-slate-50/50" : ""}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="py-2 px-3 text-slate-700 whitespace-nowrap">{renderCell(c, r)}</td>
                  ))}
                  {showAttachments && (
                    <td className="py-2 px-3 text-center whitespace-nowrap">
                      {r.attachments > 0 ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#082B4D] font-medium">
                          <Paperclip className="w-3 h-3" /> {r.attachments}
                        </span>
                      ) : <span className="text-slate-300 text-[11px]">—</span>}
                    </td>
                  )}
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    {recordIsLocked(r) ? (
                      <span data-testid={`${storeKey}-locked-${rowId(r)}`} className="text-[10px] font-semibold text-slate-400 px-1" title="Record is locked and cannot be edited.">Locked</span>
                    ) : (
                      <button data-testid={`${storeKey}-edit-${rowId(r)}`} className="text-slate-500 hover:text-[#082B4D] p-1" onClick={() => beginEdit(r)} title="View / Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button data-testid={`${storeKey}-delete-${rowId(r)}`} className="text-slate-500 hover:text-rose-500 p-1" onClick={() => remove(rowId(r))} title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-slate-100 text-[11px] text-slate-500 flex justify-between">
          <span>{filtered.length} of {rows.length} records</span>
          <span className="font-mono">ISO 45001 · Controlled Register</span>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(nextOpen) => {
        if (nextOpen) setOpen(true);
        else if (attachmentsBusy || saving) toast.info("Wait for record processing to finish before closing this record.");
        else cancelEdit();
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingIdentity ? "Edit" : "Add"} {title.replace(/s$/, "")}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              {formFields.map((f) => (
                <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{f.label}</label>
                  {f.type === "select" ? (
                    <select
                      data-testid={`field-${f.key}`}
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white"
                    >
                      <option value="">—</option>
                      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "textarea" ? (
                    <textarea
                      data-testid={`field-${f.key}`}
                      rows={3}
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm"
                    />
                  ) : (
                    <Input
                      data-testid={`field-${f.key}`}
                      type={f.type || "text"}
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      className="mt-1 h-9 text-sm"
                    />
                  )}
                </div>
              ))}
              {showAttachments && (
                <AttachmentUploader
                  module={storeKey}
                  recordId={rowId(editing)}
                  attachments={Array.isArray(editing.attachmentsList) ? editing.attachmentsList : []}
                  onBusyChange={setAttachmentsBusy}
                  onChange={(attachmentsList) => setEditing((current) => ({
                    ...current,
                    attachmentsList,
                    attachments: attachmentsList.length,
                  }))}
                />
              )}

            </div>
          )}
          {saveError && <div role="alert" data-testid="register-save-error" className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">{saveError}</div>}
          <DialogFooter>
            <Button variant="outline" disabled={attachmentsBusy || saving} onClick={cancelEdit}>Cancel</Button>
            <Button data-testid="dialog-save-btn" disabled={attachmentsBusy || saving} onClick={save} className="text-white" style={{ background: "#18B84F" }}>{attachmentsBusy ? "Attachment processing…" : saving ? "Saving…" : editingIdentity ? "Save changes" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Pill = ({ value, type }) => {
  const map = type || {};
  const m = map[value] || { bg: "#F1F5F9", color: "#455560" };
  return <span className="cmd-pill" style={{ background: m.bg, color: m.color }}>{value}</span>;
};

// Common badge palettes exported for reuse.
export const STATUS_BADGES = {
  Open: { bg: "#FEF3C7", color: "#854D0E" },
  "In Progress": { bg: "#DBEAFE", color: "#1E40AF" },
  Closed: { bg: "#DCFCE7", color: "#166534" },
  Overdue: { bg: "#FEE2E2", color: "#7F1D1D" },
  Approved: { bg: "#DCFCE7", color: "#166534" },
  Draft: { bg: "#F1F5F9", color: "#334155" },
  Submitted: { bg: "#DBEAFE", color: "#1E40AF" },
  Locked: { bg: "#082B4D", color: "#ffffff" },
  Expired: { bg: "#FEE2E2", color: "#7F1D1D" },
  Rejected: { bg: "#F1F5F9", color: "#334155" },
  "Under Review": { bg: "#DBEAFE", color: "#1E40AF" },
  Superseded: { bg: "#F1F5F9", color: "#334155" },
  Valid: { bg: "#DCFCE7", color: "#166534" },
  "Expiring Soon": { bg: "#FEF3C7", color: "#854D0E" },
  Compliant: { bg: "#DCFCE7", color: "#166534" },
  "Non-Compliant": { bg: "#FEE2E2", color: "#7F1D1D" },
  Partial: { bg: "#FEF3C7", color: "#854D0E" },
  Active: { bg: "#DCFCE7", color: "#166534" },
  Inactive: { bg: "#F1F5F9", color: "#334155" },
  Suspended: { bg: "#FEF3C7", color: "#854D0E" },
  Recorded: { bg: "#DBEAFE", color: "#1E40AF" },
  Verified: { bg: "#DCFCE7", color: "#166534" },
};

export const PRIORITY_BADGES = {
  Low: { bg: "#DCFCE7", color: "#166534" },
  Medium: { bg: "#FEF3C7", color: "#854D0E" },
  High: { bg: "#FFE4E6", color: "#9F1239" },
  Critical: { bg: "#FEE2E2", color: "#7F1D1D" },
};

export const RATING_BADGES = {
  "A+": { bg: "#DCFCE7", color: "#166534" },
  A: { bg: "#DCFCE7", color: "#166534" },
  B: { bg: "#FEF3C7", color: "#854D0E" },
  C: { bg: "#FFE4E6", color: "#9F1239" },
  D: { bg: "#FEE2E2", color: "#7F1D1D" },
};

// Reusable KPI stat card.
export function StatCard({ label, value, sub, tone = "neutral", testid }) {
  const c = { safe: "#18B84F", warning: "#F59E0B", critical: "#EF4444", info: "#2563EB", neutral: "#082B4D" }[tone] || "#082B4D";
  return (
    <div className="command-card p-3.5 border-l-4" style={{ borderLeftColor: c }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-heading font-bold text-2xl tabular text-[#0E2336] mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
